import { type CSSProperties } from 'react';
import {
    rectSortingStrategy,
    type SortingStrategy,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* The reorder's rules and its hook, apart from the components (Sortable.tsx), so the moves are
   tested without a drag and fast refresh keeps working on the components. */

/** How a sortable's entries sit: rows, or tiles that wrap. */
export type SortableLayout = 'list' | 'grid';

/** dnd-kit's strategy for a layout: a grid has to be measured on both axes, a list on one. */
export function strategyFor(layout: SortableLayout): SortingStrategy {
    return layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy;
}

/** The move a drop asks for: the dragged entry's index and the index of the one it landed on. Null when nothing moves. */
export function dropMove(
    ids: readonly string[],
    activeId: string,
    overId: string | null,
): { from: number; to: number } | null {
    if (overId === null || activeId === overId) return null;
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    return from < 0 || to < 0 ? null : { from, to };
}

/** The move an arrow key on a grip asks for, or null for any other key or past either end. */
export function stepMove(
    key: string,
    index: number,
    count: number,
): { from: number; to: number } | null {
    const back = key === 'ArrowUp' || key === 'ArrowLeft';
    const forward = key === 'ArrowDown' || key === 'ArrowRight';
    if (!back && !forward) return null;
    const to = index + (back ? -1 : 1);
    return to < 0 || to >= count ? null : { from: index, to };
}

/** What lifts an entry: the grip's node and the pointer listeners dnd-kit puts on it. */
export interface SortableHandle {
    attach: (node: HTMLElement | null) => void;
    listeners: ReturnType<typeof useSortable>['listeners'];
}

export interface SortableEntry {
    /** Attach to the row or tile that moves. */
    attach: (node: HTMLElement | null) => void;
    style: CSSProperties;
    isDragging: boolean;
    /** It is the entry the dragged one will land on. */
    isOver: boolean;
    /** Hand to the SortableGrip. */
    handle: SortableHandle;
}

/** One entry of a Sortable: its node, where it is being dragged to, and the handle that lifts it. */
export function useSortableEntry(id: string): SortableEntry {
    const sortable = useSortable({ id });
    return {
        attach: sortable.setNodeRef,
        style: {
            transform: CSS.Translate.toString(sortable.transform),
            transition: sortable.transition,
        },
        isDragging: sortable.isDragging,
        isOver: sortable.isOver && !sortable.isDragging,
        handle: { attach: sortable.setActivatorNodeRef, listeners: sortable.listeners },
    };
}
